import { useState } from 'react';
import type { Project } from '@/types';
import { formatDate } from '@/lib/utils';

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (projectId: string) => void;
}

export const ProjectCard = ({ project, onEdit, onDelete }: ProjectCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusBadge = () => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'アクティブ' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: '完了' },
      archived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'アーカイブ' },
    };

    const badge = badges[project.status] || badges.active;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

      <div className="relative card border-2 border-gray-100 group-hover:border-primary-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {project.code && (
                <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                  {project.code}
                </span>
              )}
              {getStatusBadge()}
            </div>
            <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
              {project.name}
            </h3>
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Client Info */}
        {project.client && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>{project.client.name}</span>
          </div>
        )}

        {/* Description */}
        {project.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-4">{project.description}</p>
        )}

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 animate-fadeIn">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 mb-1">作成日</p>
                <p className="text-gray-900 font-medium">
                  {project.createdAt ? formatDate(project.createdAt.toDate()) : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">更新日</p>
                <p className="text-gray-900 font-medium">
                  {project.updatedAt ? formatDate(project.updatedAt.toDate()) : '-'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={() => onEdit(project)}
            className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 active:scale-95 transition-all"
          >
            編集
          </button>
          <button
            onClick={() => onDelete(project.id)}
            className="px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 active:scale-95 transition-all"
          >
            削除
          </button>
        </div>
      </div>
    </div>
  );
};
